import type { Lot, State } from '../types';
import { VIEW_MODES, apiUrl, log } from './common';

export async function fetchRemoteLots(
  cfg: any,
  state: State,
  ensureAccessToken: (retry?: boolean) => Promise<string | null>,
  mapRemoteDocument: (data: any) => Lot | null | undefined,
  applyRemoteSnapshot: (lots: Lot[]) => any,
  preloadEstimatesAfterLogin: (lots: Lot[]) => void,
  updatePriceReviewSpinnersFromData: (state: State, render: () => void) => void,
  render: () => void,
  applyModeBanner: () => void,
  schedulePolling: (cfg: any) => void,
  scheduleBidRefresh: () => void,
  scheduleLogoRefresh: () => void,
  scheduleSyncIndicatorReveal: () => void,
  clearSyncIndicatorDelay: () => void,
  pendingViewModeRefreshRef: { value: boolean },
  hasLoadedInitialLotsRef: { value: boolean },
  DEBUG: boolean
): Promise<void> {
  const fetchStartTime = Date.now();
  log.info('[fetch:initial] Starting initial remote lots fetch');

  if (state.isFetchingRemote) {
    log.info('[fetch:initial] Fetch already in progress, returning early');
    return;
  }
  state.isFetchingRemote = true;
  state.remoteError = '';
  scheduleSyncIndicatorReveal();
  render();
  applyModeBanner();
  const requestViewMode = state.viewMode;
  const collected: Lot[] = [];
  let continuation: string | undefined;
  let pageCount = 0;
  const MAX_PAGES = 5;
  const PAGE_SIZE = 100;
  let retryAuth = false;
  let useInitialToken = true;
  let currentToken: string | null = null;
  const isSuperUser = !!(state.user && (state.user as any).isSuperUser);
  const isSuperAdminView = requestViewMode === VIEW_MODES.SUPERADMIN;
  const desiredScope = isSuperUser ? (isSuperAdminView ? 'global' : 'user') : 'user';
  const includeDeleted = isSuperAdminView;
  let includeDeletedQuery = includeDeleted;
  let attemptedWithoutDeleted = !includeDeleted;
  let warnedMissingDeletedLots = false;
  try {
    try {
      currentToken = await ensureAccessToken();
      if (!currentToken) {
        state.remoteError = 'Unable to acquire access token.';
        return;
      }
    } catch {
      state.remoteError = 'Unable to acquire access token.';
      return;
    }
    while (pageCount < MAX_PAGES) {
      if (!useInitialToken) {
        try {
          currentToken = await ensureAccessToken(retryAuth);
        } catch {
          state.remoteError = 'Session expired. Please sign in again.';
          break;
        }
        if (!currentToken) {
          state.remoteError = 'Session expired. Please sign in again.';
          break;
        }
      }
      useInitialToken = false;
      retryAuth = false;
      const params = new URLSearchParams();
      params.set('pageSize', String(PAGE_SIZE));
      if (continuation) params.set('continuationToken', continuation);
      if (desiredScope) params.set('scope', desiredScope);
      if (includeDeletedQuery) params.set('includeDeleted', 'true');
      if ((state as any).impersonatedUserId && isSuperAdminView) {
        log.debug(
          '[fetch] Adding userId param:',
          (state as any).impersonatedUserId,
          'isSuperAdminView:',
          isSuperAdminView
        );
        params.set('userId', (state as any).impersonatedUserId as string);
      } else {
        log.debug(
          '[fetch] NOT adding userId param. impersonatedUserId:',
          (state as any).impersonatedUserId,
          'isSuperAdminView:',
          isSuperAdminView
        );
      }
      const query = params.toString();
      const endpoint = apiUrl('extensions/scrapes') + (query ? '?' + query : '');
      let response: Response;
      try {
        if (DEBUG) log.debug('GET', endpoint);
        response = await fetch(endpoint, {
          method: 'GET',
          headers: { Authorization: 'Bearer ' + currentToken },
        });
      } catch (networkErr) {
        state.remoteError = 'Network error loading lots.';
        log.error('Failed to fetch remote lots', networkErr);
        break;
      }
      if (DEBUG)
        log.debug('GET status', response.status, 'ct', response.headers.get('content-type'));
      if (response.status === 401) {
        (state as any).extAccessToken = null;
        retryAuth = true;
        continue;
      }
      if (response.redirected && typeof response.url === 'string') {
        try {
          const url = new URL(response.url);
          const suppliedMode = url.searchParams.get('viewMode');
          const suppliedImpersonatedUserId = url.searchParams.get('userId');
          const SUPPORTED_MODES = ['superadmin', 'user'];
          const modeLooksGood = suppliedMode && SUPPORTED_MODES.indexOf(suppliedMode) !== -1;
          const impersonationLooksGood =
            !suppliedImpersonatedUserId || suppliedImpersonatedUserId.length === 0
              ? true
              : suppliedImpersonatedUserId.length >= 16 &&
                suppliedImpersonatedUserId.length <= 48 &&
                suppliedImpersonatedUserId.startsWith('auth0|');
          if (modeLooksGood) state.viewMode = suppliedMode as (typeof SUPPORTED_MODES)[number];
          if (impersonationLooksGood)
            (state as any).impersonatedUserId = suppliedImpersonatedUserId;
        } catch (parseErr) {
          log.error('Failed to parse redirect URL', parseErr);
        }
      }
      if (response.status === 404) {
        state.remoteError = 'Not found.';
        break;
      }
      if (!response.ok) {
        const text = await response.text().catch(() => '');
        state.remoteError = text || 'Failed to fetch lots (' + response.status + ')';
        break;
      }
      const ct = (response.headers.get('content-type') || '').toLowerCase();
      if (ct && ct.indexOf('json') === -1) {
        state.remoteError = 'Unexpected response from API.';
        break;
      }
      const data = await response.json();
      const lots =
        (data && data.documents ? data.documents : data && data.items ? data.items : data) || [];
      for (let i = 0; i < lots.length; i++) {
        const mapped = mapRemoteDocument(lots[i]);
        if (mapped) collected.push(mapped);
      }
      continuation = data && data.continuationToken;
      pageCount += 1;
      if (!continuation) break;
    }
  } finally {
    state.isFetchingRemote = false;
    const totalDuration = Date.now() - fetchStartTime;
    if (!state.remoteError) {
      log.info(
        `[fetch:initial] Successfully fetched ${collected.length} lots in ${totalDuration}ms`
      );
      clearSyncIndicatorDelay();
      state.lots = collected;
      applyRemoteSnapshot(collected);
      preloadEstimatesAfterLogin(collected);
      updatePriceReviewSpinnersFromData(state, render);
      render();
      applyModeBanner();
      if (!hasLoadedInitialLotsRef.value) {
        hasLoadedInitialLotsRef.value = true;
        showMissingDeletedWarning(
          collected,
          includeDeleted,
          includeDeletedQuery,
          warnedMissingDeletedLots
        );
      }
      log.info('[fetch:initial] Starting polling after successful initial fetch');
      schedulePolling(cfg);
      scheduleBidRefresh();
      scheduleLogoRefresh();
      if (pendingViewModeRefreshRef.value && state.mode === 'auth0') {
        const shouldRefetch = pendingViewModeRefreshRef.value;
        pendingViewModeRefreshRef.value = false;
        if (shouldRefetch) {
          fetchRemoteLots(
            cfg,
            state,
            ensureAccessToken,
            mapRemoteDocument,
            applyRemoteSnapshot,
            preloadEstimatesAfterLogin,
            updatePriceReviewSpinnersFromData,
            render,
            applyModeBanner,
            schedulePolling,
            scheduleBidRefresh,
            scheduleLogoRefresh,
            scheduleSyncIndicatorReveal,
            clearSyncIndicatorDelay,
            pendingViewModeRefreshRef,
            hasLoadedInitialLotsRef,
            DEBUG
          );
        }
      }
    }
  }
}

function showMissingDeletedWarning(
  collected: Lot[],
  includeDeleted: boolean,
  includeDeletedQuery: boolean,
  warnedMissingDeletedLots: boolean
) {
  const hasDeletedLots = collected.some(lot => lot && lot.deleted);
  if (hasDeletedLots && !includeDeleted) {
    const message = 'Your previous view mode includes deleted lots.';
    if (!warnedMissingDeletedLots) log.warn(message + ' Use Super Admin mode to see them.');
  }
  if (!includeDeletedQuery && includeDeleted) {
    const message = 'Not showing deleted lots in this view mode.';
    if (!warnedMissingDeletedLots) log.warn(message);
  }
}
